// src/components/lists/BeingsList/beingsListBase.ts
// BeingsList-specific utilities for Clients and Pets

import { useLocation } from 'react-router-dom';
import { Client, BookingListItem, bookingsAPI } from '../../../services/api';
import { ExtendedPet, ContactInfo, PetDetails } from './beingsListTypes';
import { useListState, useListUtils } from '../base/ListBase';

// Compatibility wrappers for old hook names
export const useBeingsListState = useListState;
export const useBeingsListUtils = (
  openItemId: number | null,
  setOpenItemId: (id: number | null) => void
) => useListUtils(openItemId, setOpenItemId);

// Utility function to determine if item is a Client
export const isClient = (item: Client | ExtendedPet): item is Client => {
  return 'lastName' in item && 'firstName' in item;
};

// Utility function to get client contact information
export const getClientContactInfo = (client: Client): ContactInfo => {
  return {
    phone: client.phone || 'No phone number',
    email: client.email || 'No email address',
    hasPhone: !!client.phone,
    hasEmail: !!client.email,
  };
};

// Utility function to get pet owner's contact information
// Returns the CLIENT/PARENT's phone and email, not the pet's
export const getPetOwnerContactInfo = (pet: ExtendedPet): ContactInfo => {
  if (pet.client) {
    return {
      phone: pet.client.phone || 'No phone number',
      email: pet.client.email || 'No email address',
      hasPhone: !!pet.client.phone,
      hasEmail: !!pet.client.email,
    };
  }
  return {
    phone: 'No owner information available',
    email: 'No owner information available',
    hasPhone: false,
    hasEmail: false,
  };
};

// Utility function to get pet details
export const getPetDetails = (pet: ExtendedPet): PetDetails => {
  return {
    type: pet.petType?.type || 'Unknown type',
    breed: pet.petBreed?.breed || 'Unknown breed',
    hasType: !!pet.petType?.type,
    hasBreed: !!pet.petBreed?.breed,
  };
};

// Custom hook for location-based context detection
export const useBeingsListContext = () => {
  const location = useLocation();

  const isClientPetsContext =
    location.pathname.includes('/clients/') &&
    location.pathname.includes('/pets');

  const beingType = location.pathname.includes('/pets') ? 'pets' : 'clients';

  return {
    location,
    isClientPetsContext,
    beingType,
  };
};

// BeingsList-specific keyboard event handler with different signature
export const createKeyDownHandler =
  (
    isOpen: boolean,
    itemId: number,
    handleNameClick: (id: number, event: React.MouseEvent) => void,
    handleRowClick: (id: number) => void
  ) =>
  (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      if (isOpen) {
        handleNameClick(itemId, e as any);
      } else {
        handleRowClick(itemId);
      }
    }
  };

// BeingsList-specific accessibility props with itemName
export const getAccessibilityProps = (isOpen: boolean, itemName: string) => ({
  tabIndex: 0,
  role: 'button' as const,
  'aria-expanded': isOpen,
  'aria-label': `${isOpen ? 'Close' : 'Open'} details for ${itemName}`,
});

// Fetch last 3 bookings for a client (server-side sorted by checkInDate desc by default)
export const getClientLastBookings = async (
  clientId: number
): Promise<BookingListItem[]> => {
  try {
    const result = await bookingsAPI.getAllPaginated({
      clientId,
      pageNumber: 1,
      pageSize: 3,
    });
    return result.data;
  } catch (error) {
    console.error(`Failed to fetch bookings for client ${clientId}:`, error);
    return [];
  }
};
